import { FlexWrapper, Input, notify } from '@affine/component';
import { SettingHeader, SettingRow, } from '@affine/component/setting-components';
import { Avatar } from '@affine/component/ui/avatar';
import { Button } from '@affine/component/ui/button';
import { useSignOut } from '@affine/core/components/hooks/affine/use-sign-out';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useCatchEventCallback } from '@affine/core/components/hooks/use-catch-event-hook';
import { Upload } from '@affine/core/components/pure/file-upload';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import { SubscriptionPlan } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ArrowRightSmallIcon, CameraIcon } from '@blocksuite/icons/rc';
import { useEnsureLiveData, useLiveData, useService, useServices, } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthService, FetchService, ServerService } from '../../../../modules/cloud';
import type { SettingState } from '../types';
import { AIUsagePanel } from './ai-usage-panel';
import { StorageProgress } from './storage-progress';
import * as styles from './style.css';
import { Flex, Tag } from 'antd'
import { FAntdInput } from 'izid'
import { PlusOutlined } from '@ant-design/icons'

export const UserAvatar = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useEnsureLiveData(session.account$);

  const handleUpdateUserAvatar = useAsyncCallback(
    async (file: File) => {
      try {
        track.$.settingsPanel.accountSettings.uploadAvatar();
        await session.uploadAvatar(file);
        notify.success({ title: 'Update user avatar success' });
      } catch (e) {
        // TODO(@catsjuice): i18n
        notify.error({
          title: 'Update user avatar failed',
          message: String(e),
        });
      }
    },
    [session]
  );

  const handleRemoveUserAvatar = useCatchEventCallback(async () => {
    track.$.settingsPanel.accountSettings.removeAvatar();
    await session.removeAvatar();
  }, [session]);

  return (
    <Upload
      accept="image/gif,image/jpeg,image/jpg,image/png,image/svg"
      fileChange={handleUpdateUserAvatar}
      data-testid="upload-user-avatar"
    >
      <Avatar
        size={56}
        name={account.label}
        url={account.avatar}
        hoverIcon={<CameraIcon />}
        onRemove={account.avatar ? handleRemoveUserAvatar : undefined}
        avatarTooltipOptions={{ content: t['Click to replace photo']() }}
        removeTooltipOptions={{ content: t['Remove photo']() }}
        data-testid="user-setting-avatar"
        removeButtonProps={{
          ['data-testid' as string]: 'user-setting-remove-avatar-button',
        }}
      />
    </Upload>
  );
};

export const AvatarAndName = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useEnsureLiveData(session.account$);
  const [input, setInput] = useState<string>(account.label);

  const allowUpdate = !!input && input !== account.label;
  const handleUpdateUserName = useAsyncCallback(async () => {
    if (account === null) {
      return;
    }
    if (!allowUpdate) {
      return;
    }

    try {
      track.$.settingsPanel.accountSettings.updateUserName();
      await session.updateLabel(input);
    } catch (e) {
      notify.error({
        title: 'Failed to update user name.',
        message: String(e),
      });
    }
  }, [account, allowUpdate, session, input]);

  return (
    <SettingRow
      name={t['com.affine.settings.profile']()}
      desc={t['com.affine.settings.profile.message']()}
      spreadCol={false}
    >
      <FlexWrapper style={{ margin: '12px 0 24px 0' }} alignItems="center">
        <UserAvatar />

        <div className={styles.profileInputWrapper}>
          <label>{t['com.affine.settings.profile.name']()}</label>
          <FlexWrapper alignItems="center">
            <Input
              defaultValue={input}
              data-testid="user-name-input"
              placeholder={t['com.affine.settings.profile.placeholder']()}
              maxLength={64}
              minLength={0}
              style={{ width: 280, height: 32 }}
              onChange={setInput}
              onEnter={handleUpdateUserName}
            />
            {allowUpdate ? (
              <Button
                data-testid="save-user-name"
                onClick={handleUpdateUserName}
                style={{
                  marginLeft: '12px',
                }}
              >
                {t['com.affine.editCollection.save']()}
              </Button>
            ) : null}
          </FlexWrapper>
        </div>

      </FlexWrapper>
    </SettingRow>
  );
};

const StoragePanel = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();

  const onUpgrade = useCallback(() => {
    track.$.settingsPanel.accountUsage.viewPlans({
      plan: SubscriptionPlan.Pro,
    });
    onChangeSettingState?.({
      activeTab: 'plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [onChangeSettingState]);

  return (
    <SettingRow
      name={t['com.affine.storage.title']()}
      desc=""
      spreadCol={false}
    >
      <StorageProgress onUpgrade={onUpgrade} />
    </SettingRow>
  );
};

export const AccountSetting = ({
  onChangeSettingState,
  onCloseSetting
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const { authService, serverService, globalDialogService } = useServices({
    AuthService,
    ServerService,
    GlobalDialogService,
  });
  const fetchService = useService(FetchService);
  const serverFeatures = useLiveData(serverService.server.features$);
  const t = useI18n();
  const session = authService.session;
  useEffect(() => {
    session.revalidate();
  }, [session]);
  const account = useEnsureLiveData(session.account$);
  const openSignOutModal = useSignOut({
    onConfirm () {
      onCloseSetting?.()
    }
  });

  const onChangeEmail = useCallback(() => {
    globalDialogService.open('verify-email', {
      server: serverService.server.baseUrl,
      changeEmail: !!account.info?.emailVerified,
    });
  }, [account, globalDialogService, serverService.server.baseUrl]);

  const onPasswordButtonClick = useCallback(() => {
    onCloseSetting?.()
    globalDialogService.open('antd-change-pwd')
    // globalDialogService.open('change-password', {
    //   server: serverService.server.baseUrl,
    // });
  }, [globalDialogService, serverService.server.baseUrl]);


  // 专业领域-添加标签
  const majorTagInputRef = useRef()
  const [majorTagInputVisible, setMajorTagInputVisible] = useState<boolean>(false)
  const [majorTagInputValue, setMajorTagInputValue] = useState<string>()
  const handleMajorTagInputConfirm = async () => {
    if (majorTagInputValue && !majorTag.includes(majorTagInputValue)) {
      if (majorTagInputValue.length > 5) {
        majorTagInputRef.current?.focus();
        return notify.warning({
          title: '标签字数不能超过五个字'
        })
      }
      if (majorTag.length >= 4) {
        majorTagInputRef.current?.focus();
        return notify.warning({
          title: '最多只能添加四个标签'
        })
      }
      const newTags = [...majorTag, majorTagInputValue]
      await session.updateTags(newTags)
      setMajorTag(newTags)
    }
    setMajorTagInputVisible(false);
    setMajorTagInputValue('');
  };
  useEffect(() => {
    if (majorTagInputVisible) {
      majorTagInputRef.current?.focus();
    }
  }, [majorTagInputVisible]);

  // 专业领域-编辑标签
  const majorTagEditInputRef = useRef()
  const [majorTagEditInputIndex, setMajorTagEditInputIndex] = useState(-1)
  const [majorTagEditInputValue, setMajorTagEditInputValue] = useState<string>()
  const handleMajorTagEditInputConfirm = async () => {
    const newTags = [...majorTag];
    if (majorTagEditInputValue.length > 5) {
      majorTagEditInputRef.current?.focus()
      return notify.warning({
        title: '标签字数不能超过五个字'
      })
    }
    if (majorTagEditInputValue.length) {
      newTags[majorTagEditInputIndex] = majorTagEditInputValue;
    }
    await session.updateTags(newTags)
    setMajorTag(newTags);
    setMajorTagEditInputIndex(-1);
    setMajorTagEditInputValue('');
  }
  useEffect(() => {
    majorTagEditInputRef.current?.focus();
  }, [majorTagEditInputValue]);

  // 专业领域-删除标签
  const handleCloseMajorTag = async deleteTag => {
    const newTags = majorTag.filter(tag => tag !== deleteTag);
    await session.updateTags(newTags)
    setMajorTag(newTags);
  }

  // 专业领域
  const majorTagColor = [
    'magenta',
    'red',
    'volcano',
    'orange',
  ]
  const [majorTag, setMajorTag] = useState([])

  // 编辑介绍
  const [introduction, setIntroduction] = useState<string>('')
  const introductionInputRef = useRef()
  const [introductionInputVisible, setIntroductionInputVisible] = useState(false)
  const [introductionInputValue, setIntroductionInputValue] = useState<string>('')
  const toEditIntroduction = () => {
    setIntroductionInputVisible(true)
    setIntroductionInputValue(introduction)
  }
  useEffect(() => {
    introductionInputRef.current?.focus();
  }, [introductionInputValue]);
  const onSaveIntroduction = async () => {
    await session.updateIntroduction(introductionInputValue)
    setIntroduction(introductionInputValue)
    setIntroductionInputVisible(false)
    setIntroductionInputValue('')
  }

  // 密级
  const [securityLevel, setSecurityLevel] = useState<string>('')
  const securityColorWrapper = {
    'Public': {
      label: '公开',
      color: '#2db7f5'
    },
  }
  const getSecurityRender = () => {
    if (securityLevel in securityColorWrapper) return securityColorWrapper[securityLevel]
    return {
      label: '公开',
      color: '#2db7f5'
    }
  }

  // 任务权限
  const [userTaskPermission, setUserTaskPermission] = useState<string>('All')
  const userTaskPermissionWrapper = {
    'All': '全部'
  }

  // 积分等级
  const [pointLevel, setPointLevel] = useState<number>(0)
  const [points, setPoints] = useState(0)
  const pointsLadder = [100, 200, 300, 400, 500]
  const getPointLevel = _points => {
    let level = 0
    const increaseLevel = () => {
      if (pointsLadder[level] <= _points) {
        level++
        if (level !== pointsLadder.length) {
          increaseLevel()
        }
      }
    }
    increaseLevel()
    setPointLevel(level)
  }
  const initPoints = points => {
    setPoints(points)
    getPointLevel(points)
  }

  useEffect(() => {
    // 个人资料初始化
    setIntroduction(account.info?.introduction)
    setMajorTag(account.info?.professionalFields)
    // 获取积分等级
    fetchService.fetch('/api/avatars/user/info', {
      headers: {
        'Content-Type': 'application/json'
      }
    }).then(res => res.json()).then(res => {
      initPoints(res.data.pointBalance)
    })
  }, [account]);

  return (
    <>
      <SettingHeader
        title={ t['com.affine.setting.account']() }
        subtitle={ t['com.affine.setting.account.message']() }
        data-testid="account-title"
      />
      <AvatarAndName />
      <SettingRow name={ t['com.affine.settings.email']() } desc={ account.email }>
        {/* <Button onClick={onChangeEmail}> */ }
        {/*   {account.info?.emailVerified */ }
        {/*     ? t['com.affine.settings.email.action.change']() */ }
        {/*     : t['com.affine.settings.email.action.verify']()} */ }
        {/* </Button> */ }
      </SettingRow>

      <SettingRow name={ '介绍' } desc={
        introductionInputVisible ?
          <FAntdInput.TextArea
            ref={ introductionInputRef }
            style={ { height: 50 } }
            state={ [ introductionInputValue, setIntroductionInputValue ] }
          /> :
          <>
            { introduction || '暂无' }
          </>
      }>
        {
          introductionInputVisible ?
            <Button onClick={ onSaveIntroduction }>
              保存介绍
            </Button> :
            <Button onClick={ toEditIntroduction }>
              编辑介绍
            </Button>
        }
      </SettingRow>
      <SettingRow
        name={ '密级' }
        desc={ <>
          <Tag color={ getSecurityRender().color }>{ getSecurityRender().label }</Tag>
        </> }
      >
      </SettingRow>
      <SettingRow
        name={ '任务权限' }
        desc={ userTaskPermissionWrapper[userTaskPermission] || '全部' }
      >
      </SettingRow>
      <SettingRow
        name={ '积分等级' }
        spreadCol={ false }
      >
        <Flex justify={ 'space-between' } style={ {
          color: 'var(--affine-text-secondary-color)',
          fontSize: 'var(--affine-font-xs)'
        } }>
          <span>{ pointLevel }级</span>
          {
            pointLevel <= pointsLadder.length - 1 &&
            <span>{ pointLevel + 1 }级 { points }/{ pointsLadder[pointLevel] }</span>
          }
        </Flex>
        <div style={ {
          background: 'var(--affine-black-10)',
          height: '8px',
          borderRadius: '4px',
          overflow: 'hidden',
        } }>
          <div style={ {
            width: `${ points / pointsLadder[Math.min(pointLevel, pointsLadder.length - 1)] * 100 }%`,
            height: '100%',
            background: 'var(--affine-processing-color)'
          } } />
        </div>
      </SettingRow>
      <SettingRow name={ '专业领域' } desc={ '擅长的领域标签' }>
        <Flex align={ 'center' }>
          {
            majorTag.map((r, index) => {
              if (index === majorTagEditInputIndex) return <FAntdInput
                state={ [ majorTagEditInputValue, setMajorTagEditInputValue ] }
                key={ r }
                style={ {
                  width: 80,
                  marginInlineEnd: 8,
                } }
                size="small"
                ref={ majorTagEditInputRef }
                placeholder={ '' }
                onBlur={ handleMajorTagEditInputConfirm }
                onPressEnter={ handleMajorTagEditInputConfirm }
              />
              return <Tag
                closable
                key={ r }
                style={ { userSelect: 'none' } }
                color={ majorTagColor[index] }
                onClose={ () => handleCloseMajorTag(r) }
              >
                <span
                  onDoubleClick={ e => {
                    setMajorTagEditInputIndex(index);
                    setMajorTagEditInputValue(r);
                    e.preventDefault();
                  } }
                >
                  { r }
                </span>
              </Tag>
            })
          }
          { majorTagInputVisible ? (
            <FAntdInput
              ref={ majorTagInputRef }
              type="text"
              size="small"
              style={ {
                width: 80,
                marginInlineEnd: 8,
              } }
              placeholder={ '' }
              state={ [ majorTagInputValue, setMajorTagInputValue ] }
              onBlur={ handleMajorTagInputConfirm }
              onPressEnter={ handleMajorTagInputConfirm }
            />
          ) : (
            majorTag.length < 4 &&
            <Tag
              style={ {
                height: 22,
                borderStyle: 'dashed',
              } }
              icon={ <PlusOutlined /> }
              onClick={ () => setMajorTagInputVisible(true) }
            >
              新标签
            </Tag>
          ) }
        </Flex>
      </SettingRow>
      <SettingRow
        name={ t['com.affine.settings.password']() }
        desc={ t['com.affine.settings.password.message']() }
      >
        <Button onClick={onPasswordButtonClick}>
          {account.info?.hasPassword
            ? t['com.affine.settings.password.action.change']()
            : t['com.affine.settings.password.action.set']()}
        </Button>
      </SettingRow>
      <StoragePanel onChangeSettingState={ onChangeSettingState } />
      { serverFeatures?.copilot && (
        <AIUsagePanel onChangeSettingState={ onChangeSettingState } />
      ) }
      <SettingRow
        name={ t[`Sign out`]() }
        desc={ t['com.affine.setting.sign.out.message']() }
        style={ { cursor: 'pointer' } }
        data-testid="sign-out-button"
        onClick={ openSignOutModal }
      >
        <ArrowRightSmallIcon />
      </SettingRow>
      {/*<SettingRow*/ }
      {/*  name={*/ }
      {/*    <span style={{ color: 'var(--affine-warning-color)' }}>*/ }
      {/*      {t['com.affine.setting.account.delete']()}*/ }
      {/*    </span>*/ }
      {/*  }*/ }
      {/*  desc={t['com.affine.setting.account.delete.message']()}*/ }
      {/*  style={{ cursor: 'pointer' }}*/ }
      {/*  onClick={useCallback(() => {*/ }
      {/*    toast('Function coming soon');*/ }
      {/*  }, [])}*/ }
      {/*  testId="delete-account-button"*/ }
      {/*>*/ }
      {/*  <ArrowRightSmallIcon />*/ }
      {/*</SettingRow>*/ }
    </>
  );
};
